import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { subnetNames } from '../../utils/subnetNames';
import { generateHolderAnalysis } from '../openaiService';
import { formatDistanceToNow } from 'date-fns';

interface HolderStats {
  categories: {
    Whale: CategoryData;
    Shark: CategoryData;
    Dolphin: CategoryData;
    Shrimp: CategoryData;
    [key: string]: CategoryData;
  };
  hhi: number;
  holders: number;
  timestamp: string;
  pool_price?: string;
}

interface CategoryData {
  count: number;
  total_balance: number;
  total_balance_usd: number;
}

export async function getHolderReport(netuid: number, hours: number): Promise<string> {
  try {
    const statsRef = collection(db, 'netuid_stats', netuid.toString(), 'entries');
    const startTime = new Date();
    startTime.setHours(startTime.getHours() - hours);
    
    const q = query(
      statsRef,
      where('timestamp', '>=', startTime.toISOString()),
      orderBy('timestamp', 'asc')
    );
    
    const snapshot = await getDocs(q);
    const entries = snapshot.docs.map(doc => doc.data() as HolderStats);
    
    if (entries.length < 2) {
      const latestSnapshot = await getDocs(query(statsRef, orderBy('timestamp', 'desc'), limit(1)));
      if (!latestSnapshot.empty) {
        const latestData = latestSnapshot.docs[0].data() as HolderStats;
        const report = `Current Holder Distribution for Subnet ${netuid} (${subnetNames[netuid]})

Total Holders: ${latestData.holders.toLocaleString()}
Pool Price: ${latestData.pool_price || 'N/A'} τ

Distribution by Category:
${Object.entries(latestData.categories).map(([category, data]) => 
  `${category}s:
  Count: ${data.count.toLocaleString()}
  Balance: ${data.total_balance.toFixed(2)} τ ($${data.total_balance_usd.toFixed(2)})`
).join('\n\n')}

HHI (Market Concentration): ${latestData.hhi.toFixed(4)}`;

        const analysis = await generateHolderAnalysis(report);
        return analysis;
      }
      return `No holder data available for Subnet ${netuid} (${subnetNames[netuid]}).`;
    }

    const oldestEntry = entries[0];
    const latestEntry = entries[entries.length - 1];
    
    const calculateCategoryChanges = (category: string) => {
      const oldData = oldestEntry.categories[category];
      const newData = latestEntry.categories[category];
      if (!oldData || !newData) return null;
      
      const countChange = ((newData.count - oldData.count) / oldData.count) * 100;
      const balanceChange = ((newData.total_balance - oldData.total_balance) / oldData.total_balance) * 100;
      const usdChange = ((newData.total_balance_usd - oldData.total_balance_usd) / oldData.total_balance_usd) * 100;
      
      return {
        category,
        currentCount: newData.count,
        countChange,
        currentBalance: newData.total_balance,
        balanceChange,
        currentUsdValue: newData.total_balance_usd,
        usdChange
      };
    };

    const categoryChanges = ['Whale', 'Dolphin', 'Shrimp']
      .map(calculateCategoryChanges)
      .filter(change => change !== null);
    
    const timeAgo = formatDistanceToNow(new Date(oldestEntry.timestamp), { addSuffix: true });
    
    const report = `Holder Distribution Report for Subnet ${netuid} (${subnetNames[netuid]})
Time Period: ${timeAgo} to now

Total Holders: ${latestEntry.holders.toLocaleString()}
Change: ${((latestEntry.holders - oldestEntry.holders) / oldestEntry.holders * 100).toFixed(2)}%
Pool Price: ${latestEntry.pool_price || 'N/A'} τ

Distribution by Category:
${categoryChanges.map(change => 
  `${change!.category}s:
  Count: ${change!.currentCount.toLocaleString()} (${change!.countChange >= 0 ? '+' : ''}${change!.countChange.toFixed(2)}%)
  Balance: ${change!.currentBalance.toFixed(2)} τ (${change!.balanceChange >= 0 ? '+' : ''}${change!.balanceChange.toFixed(2)}%)
  USD Value: $${change!.currentUsdValue.toFixed(2)} (${change!.usdChange >= 0 ? '+' : ''}${change!.usdChange.toFixed(2)}%)`
).join('\n\n')}

HHI (Market Concentration): ${latestEntry.hhi.toFixed(4)}`;

    const analysis = await generateHolderAnalysis(report);
    return analysis;
  } catch (error) {
    console.error('Error generating holder report:', error);
    return 'Error: Failed to generate holder distribution report.';
  }
}

export async function getTopByHolders(limit: number = 5, hours: number = 24): Promise<string> {
  try {
    const subnets = Object.keys(subnetNames).map(id => parseInt(id));
    const results = await Promise.all(
      subnets.map(async (netuid) => {
        const statsRef = collection(db, 'netuid_stats', netuid.toString(), 'entries');
        const snapshot = await getDocs(query(statsRef, orderBy('timestamp', 'desc'), limit(1)));
        
        if (snapshot.empty) return null;
        
        const latestData = snapshot.docs[0].data() as HolderStats;
        return {
          netuid,
          name: subnetNames[netuid],
          holders: latestData.holders,
          hhi: latestData.hhi
        };
      })
    );
    
    const validResults = results
      .filter((result): result is NonNullable<typeof results[0]> => result !== null)
      .sort((a, b) => b.holders - a.holders)
      .slice(0, limit);
    
    return `Top ${limit} Subnets by Holder Count

${validResults.map((result, index) => 
  `${index + 1}. ${result.name} (SN${result.netuid})
   Holders: ${result.holders.toLocaleString()}
   HHI: ${result.hhi.toFixed(2)}`
).join('\n\n')}`;
  } catch (error) {
    console.error('Error getting top holders:', error);
    return 'Error: Failed to get top holders.';
  }
}

export async function getHolderDistributionForTimeRange(netuid: number, hours: number): Promise<string | null> {
  try {
    const statsRef = collection(db, 'netuid_stats', netuid.toString(), 'entries');
    const startTime = new Date();
    startTime.setHours(startTime.getHours() - hours);
    
    const q = query(
      statsRef,
      where('timestamp', '>=', startTime.toISOString()),
      orderBy('timestamp', 'asc')
    );
    
    const snapshot = await getDocs(q);
    const entries = snapshot.docs.map(doc => doc.data() as HolderStats);
    
    if (entries.length < 2) return null;

    const oldestEntry = entries[0];
    const latestEntry = entries[entries.length - 1];
    
    // Calculate significant holder movements
    const whaleChange = calculateCategoryChange('Whale', oldestEntry, latestEntry);
    const dolphinChange = calculateCategoryChange('Dolphin', oldestEntry, latestEntry);
    const shrimpChange = calculateCategoryChange('Shrimp', oldestEntry, latestEntry);
    
    const timeAgo = formatDistanceToNow(new Date(oldestEntry.timestamp), { addSuffix: true });
    
    let report = `Changes ${timeAgo} to now:\n`;
    
    // Add total holder change
    const totalHolderChange = ((latestEntry.holders - oldestEntry.holders) / oldestEntry.holders) * 100;
    report += `Total Holders: ${latestEntry.holders.toLocaleString()} (${totalHolderChange >= 0 ? '+' : ''}${totalHolderChange.toFixed(2)}%)\n\n`;
    
    // Add significant category changes
    if (whaleChange) {
      report += `Whales: ${whaleChange.currentCount} (${whaleChange.countChange >= 0 ? '+' : ''}${whaleChange.countChange.toFixed(2)}%)\n`;
      report += `Whale Balance: ${whaleChange.currentBalance.toFixed(2)} τ (${whaleChange.balanceChange >= 0 ? '+' : ''}${whaleChange.balanceChange.toFixed(2)}%)\n\n`;
    }
    
    if (dolphinChange) {
      report += `Dolphins: ${dolphinChange.currentCount} (${dolphinChange.countChange >= 0 ? '+' : ''}${dolphinChange.countChange.toFixed(2)}%)\n`;
      report += `Dolphin Balance: ${dolphinChange.currentBalance.toFixed(2)} τ (${dolphinChange.balanceChange >= 0 ? '+' : ''}${dolphinChange.balanceChange.toFixed(2)}%)\n\n`;
    }
    
    if (shrimpChange) {
      report += `Shrimps: ${shrimpChange.currentCount} (${shrimpChange.countChange >= 0 ? '+' : ''}${shrimpChange.countChange.toFixed(2)}%)\n`;
      report += `Shrimp Balance: ${shrimpChange.currentBalance.toFixed(2)} τ (${shrimpChange.balanceChange >= 0 ? '+' : ''}${shrimpChange.balanceChange.toFixed(2)}%)\n\n`;
    }
    
    // Add market concentration change
    const hhiChange = ((latestEntry.hhi - oldestEntry.hhi) / oldestEntry.hhi) * 100;
    report += `Market Concentration (HHI): ${latestEntry.hhi.toFixed(4)} (${hhiChange >= 0 ? '+' : ''}${hhiChange.toFixed(2)}%)`;
    
    return report;
  } catch (error) {
    console.error('Error getting holder distribution:', error);
    return null;
  }
}

function calculateCategoryChange(category: string, oldEntry: HolderStats, newEntry: HolderStats) {
  const oldData = oldEntry.categories[category];
  const newData = newEntry.categories[category];
  if (!oldData || !newData) return null;
  
  return {
    currentCount: newData.count,
    countChange: ((newData.count - oldData.count) / oldData.count) * 100,
    currentBalance: newData.total_balance,
    balanceChange: ((newData.total_balance - oldData.total_balance) / oldData.total_balance) * 100,
    currentUsdValue: newData.total_balance_usd,
    usdChange: ((newData.total_balance_usd - oldData.total_balance_usd) / oldData.total_balance_usd) * 100
  };
}